import { createServerFn } from "@tanstack/react-start";
import { queryOptions } from "@tanstack/react-query";
import { authMiddleware } from "~/lib/auth-middleware";
import { dbMiddleware } from "~/lib/db-middleware";
import {
  getNextFutureSummit,
  registerForSummit as registerForSummitDb,
  getUserSummitRegistration,
} from "~/lib/server/summit";
import { emailService } from "~/lib/server/email";
import { z } from "zod";

const SummitRegistrationSchema = z.object({
  firstName: z
    .string()
    .min(1, "First name is required")
    .max(100, "First name too long"),
  lastName: z
    .string()
    .min(1, "Last name is required")
    .max(100, "Last name too long"),
  email: z.string().email("Valid email is required").max(320, "Email too long"),
  redirectUrl: z
    .string()
    .url("Invalid redirect URL")
    .optional()
    .or(z.literal("")),
});

const AuthenticatedRegistrationSchema = z.object({
  redirectUrl: z
    .string()
    .url("Invalid redirect URL")
    .optional()
    .or(z.literal("")),
});

function validateRedirectUrl(url: string | undefined): string | undefined {
  if (!url || url === "") return undefined;

  try {
    const parsedUrl = new URL(url);
    // Only allow https URLs in production for security
    if (
      parsedUrl.protocol !== "https:" &&
      process.env.NODE_ENV === "production"
    ) {
      throw new Error("Only HTTPS URLs are allowed in production");
    }
    return url;
  } catch {
    throw new Error("Invalid redirect URL format");
  }
}

export const getNextAvailableSummit = createServerFn({ method: "GET" })
  .middleware([dbMiddleware])
  .handler(async () => {
    try {
      const summit = await getNextFutureSummit();
      if (!summit) {
        return { error: "No upcoming summit found", summit: null };
      }
      return { error: null, summit };
    } catch (error) {
      console.error("Error fetching next available summit:", error);
      return {
        error:
          error instanceof Error ? error.message : "Failed to fetch summit",
        summit: null,
      };
    }
  });

export const getNextAvailableSummitOptions = queryOptions({
  queryKey: ["summit", "next-available"],
  queryFn: () => getNextAvailableSummit(),
  staleTime: 5 * 60 * 1000,
});

export const checkUserSummitRegistration = createServerFn({ method: "GET" })
  .middleware([dbMiddleware, authMiddleware])
  .handler(async ({ context }) => {
    try {
      const summit = await getNextFutureSummit();
      if (!summit) {
        return { isRegistered: false, summit: null, error: null };
      }

      const registration = await getUserSummitRegistration(
        context.user.id,
        summit.id
      );

      return {
        isRegistered: registration.isRegistered,
        summit,
        error: null,
      };
    } catch (error) {
      console.error("Error checking user summit registration:", error);
      return {
        isRegistered: false,
        summit: null,
        error:
          error instanceof Error
            ? error.message
            : "Failed to check registration",
      };
    }
  });

export const checkUserSummitRegistrationOptions = queryOptions({
  queryKey: ["summit", "user-registration"],
  queryFn: () => checkUserSummitRegistration(),
  staleTime: 1 * 60 * 1000,
});

export const registerAuthenticatedUserForSummit = createServerFn({
  method: "POST",
})
  .middleware([dbMiddleware, authMiddleware])
  .validator(AuthenticatedRegistrationSchema)
  .handler(async ({ data, context }) => {
    try {
      const validatedRedirectUrl = validateRedirectUrl(data.redirectUrl);

      const summit = await getNextFutureSummit();
      if (!summit) {
        throw new Error("No upcoming summit found");
      }

      const registrationResult = await registerForSummitDb(
        context.user.id,
        summit.id
      );
      if (!registrationResult.success) {
        throw new Error("Failed to register for summit");
      }

      // Send confirmation email
      try {
        await emailService.sendSummitRegistrationConfirmation({
          user: context.user,
          summit,
        });
      } catch (emailError) {
        console.error("Failed to send summit confirmation email:", emailError);
        // Don't fail the registration if email fails
      }

      return {
        success: true,
        summit,
        redirectUrl: validatedRedirectUrl,
      };
    } catch (error) {
      console.error("Error in authenticated summit registration:", error);
      throw new Error(
        error instanceof Error ? error.message : "Failed to register for summit"
      );
    }
  });

export const sendMagicLinkRegistration = createServerFn({ method: "POST" })
  .middleware([dbMiddleware])
  .validator(SummitRegistrationSchema)
  .handler(async ({ data }) => {
    try {
      const validatedRedirectUrl = validateRedirectUrl(data.redirectUrl);

      const summit = await getNextFutureSummit();
      if (!summit) {
        throw new Error("No upcoming summit found");
      }

      // Check if registration is still open
      const now = new Date();
      const registrationCutoff = new Date(
        summit.startDate.getTime() - 30 * 60 * 1000
      );
      if (now > registrationCutoff) {
        throw new Error("Registration for this summit has closed");
      }

      // Store summit registration data in Clerk's publicMetadata for the sign-in attempt
      const redirectUrl =
        validatedRedirectUrl ||
        `${process.env.NODE_ENV === "production" ? "https://dreamcoachmatch.com" : "http://localhost:3000"}/dashboard`;

      return {
        success: true,
        summit,
        // Return the data that the frontend will use to create a Clerk sign-in attempt
        registrationData: {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          summitId: summit.id,
          redirectUrl,
        },
      };
    } catch (error) {
      console.error("Error preparing magic link registration:", error);
      throw new Error(
        `Failed to prepare registration: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    }
  });

export const completeSummitRegistrationAfterAuth = createServerFn({
  method: "POST",
})
  .middleware([dbMiddleware, authMiddleware])
  .validator(
    z.object({
      summitId: z.string().uuid(),
      redirectUrl: z.string().optional(),
    })
  )
  .handler(async ({ data, context }) => {
    try {
      // Verify summit still exists and is valid
      const summit = await getNextFutureSummit();
      if (!summit || summit.id !== data.summitId) {
        throw new Error("Summit no longer available for registration");
      }

      // Register user for summit
      const registrationResult = await registerForSummitDb(
        context.user.id,
        summit.id
      );
      if (!registrationResult.success) {
        throw new Error("Failed to register for summit");
      }

      // Send confirmation email
      try {
        await emailService.sendSummitRegistrationConfirmation({
          user: context.user,
          summit,
        });
      } catch (emailError) {
        console.error("Failed to send summit confirmation email:", emailError);
        // Don't fail the registration if email fails
      }

      return {
        success: true,
        summit,
        redirectUrl: data.redirectUrl,
      };
    } catch (error) {
      console.error("Error completing summit registration:", error);
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to complete registration"
      );
    }
  });
